import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'

export interface ApiError {
  message: string
  status: number
}

let csrfToken: string | null = null
let csrfPromise: Promise<void> | null = null

function attachCsrf(config: InternalAxiosRequestConfig) {
  if (csrfToken && config.headers) {
    config.headers['X-CSRFToken'] = csrfToken
  }
  return config
}

// ─── API client (baseURL: /api) — for data endpoints ───
const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

api.interceptors.request.use(attachCsrf, (e: AxiosError) => Promise.reject(e))
api.interceptors.response.use(
  (r: AxiosResponse) => r,
  (error: AxiosError) => {
    if (error.response?.status === 403) {
      console.error('Access denied')
    }
    return Promise.reject(error)
  },
)

// ─── Auth client (no baseURL) — for Flask auth routes (/login, /verify-totp, etc.) ───
export const authHttp: AxiosInstance = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
})

authHttp.interceptors.request.use(attachCsrf, (e: AxiosError) => Promise.reject(e))

// Fetch CSRF token from Flask once, store in memory for both axios instances
export function ensureCsrf(): Promise<void> {
  if (csrfToken) return Promise.resolve()
  if (csrfPromise) return csrfPromise
  csrfPromise = api.get('/csrf-token').then(({ data }) => {
    if (data.csrf_token) {
      csrfToken = data.csrf_token
    }
  })
  return csrfPromise
}

export function getCsrfToken(): string | null {
  return csrfToken
}

/** Extract a user-friendly error message from an Axios error */
export function getErrorMessage(err: unknown, fallback = 'Error'): string {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error ?? err.response?.data?.message ?? fallback
  }
  return fallback
}

/** Extract validation errors array from an Axios error response */
export function getValidationErrors(err: unknown): { message: string }[] | null {
  if (axios.isAxiosError(err) && Array.isArray(err.response?.data?.errors)) {
    return err.response.data.errors as { message: string }[]
  }
  return null
}

export default api
